import React from 'react';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { 
    Play,
    Code2,
    Link as LinkIcon,
    Settings,
    MoreVertical,
    Download,
    Power,
    PowerOff,
    Trash2,
    Clock
} from 'lucide-react';

import formatAudioFileDuration from '../../utils/AudioDuration';

const PodcastEpisodeItem = ({ 
    item, 
    index, 
    org_id, 
    id,
    podcast_id,
    showDetails,
    setAudio,
    setAudioTitle,
    setSelectedEpisode,
    setSelectedEpisodeIndex,
    setShowEpisodeSettings,
    setEmbedCode,
    setShowEmbedCode,
    setShowConfirmation,
    publishEpisode,
    unpublishEpisode
}) => {

    // Custom toggle for dropdown menu
    const MenuToggle = React.forwardRef(({ onClick }, ref) => (
        <button 
            type="button" 
            className="btn btn-light border-0" 
            style={{ padding: '8px 12px' }}
            ref={ref}
            onClick={(e) => {
                e.preventDefault();
                onClick(e);
            }}
        >
            <MoreVertical size={18} />
        </button>
    ));

    const PublishedBadge = ({ isPublished }) => {
        if (isPublished !== false) {
            return (
                <span className="badge text-bg-success me-3">Published</span>
            );
        }
        return (
            <span className="badge text-bg-secondary me-3">Unpublished</span>
        );
    };

    return (
        <li 
            className="list-group-item p-3" 
            style={{ borderBottom: '1px solid var(--bs-border-color)' }}
            onClick={() => {
                setSelectedEpisode(item);
                setSelectedEpisodeIndex(index);
            }}
        >
            <div className="d-flex align-items-start gap-3">
                {/* Episode Art */}
                <img 
                    alt={item.title}
                    className="flex-shrink-0" 
                    src={item.episode_art}
                    style={{ objectFit: 'cover', borderRadius: '6px' }}
                    width={96}
                    height={96}
                />
                
                {/* Content Area */}
                <div className="flex-grow-1 me-4">
                    <div className="mb-2">
                        <h4 
                            style={{ 
                            textDecoration: 'none', 
                            color: 'var(--bs-emphasis-color)',
                                fontSize: '18px',
                                fontWeight: '600',
                                marginBottom: '4px'
                            }}
                        >
                            {item.title}
                        </h4>
                        <p className="text-muted small mb-0" style={{ 
                            fontSize: '14px',
                            lineHeight: '1.5',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                        }}>
                            {item.description}
                        </p>
                    </div>
                    
                    <div className="d-flex align-items-center gap-2 flex-wrap text-muted small">
                        <PublishedBadge isPublished={item.is_published} />
                        {item.audio_duration && (
                            <span className="d-inline-flex align-items-center gap-1">
                                <Clock size={13} />
                                {formatAudioFileDuration(item.audio_duration)}
                            </span>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                    {(item.full_audio_url || item.short_audio_url) && (
                        <OverlayTrigger placement="bottom" overlay={<Tooltip>Play</Tooltip>}>
                            <button
                                type="button"
                                className="btn btn-light border-0"
                                style={{ padding: '8px 12px' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setAudio(item.full_audio_url || item.short_audio_url);
                                    setAudioTitle(item.title);
                                }}
                            >
                                <Play size={18} />
                            </button>
                        </OverlayTrigger>
                    )}
                    
                    <OverlayTrigger placement="bottom" overlay={<Tooltip>Copy Link</Tooltip>}>
                        <CopyToClipboard 
                            text={`${process.env.REACT_APP_WIDGET_BASE_URL}/inline.html?org_id=${org_id}&integration_id=${id}&${item.url ? `url=${encodeURIComponent(item.url)}` : `content_id=${item.SK.split('#')[1]}`}`} 
                            onCopy={() => toast.success("Copied to clipboard")}
                        >
                            <button
                                type="button"
                                className="btn btn-light border-0"
                                style={{ padding: '8px 12px' }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <LinkIcon size={18} />
                            </button>
                        </CopyToClipboard>
                    </OverlayTrigger>
                    
                    <OverlayTrigger placement="bottom" overlay={<Tooltip>Embed Code</Tooltip>}>
                        <button
                            type="button"
                            className="btn btn-light border-0"
                            style={{ padding: '8px 12px' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setEmbedCode(`<iframe
    id="l2it-widget-iframe"
    src="${process.env.REACT_APP_WIDGET_BASE_URL}/inline.html?org_id=${org_id}&integration_id=${id}&${item.url ? `url=${encodeURIComponent(item.url)}` : `content_id=${item.SK.split('#')[1]}`}&has_paywall=false"
    style="border:none;width:100%;height:80px;"
>
</iframe>`);
                                setShowEmbedCode(true);
                            }}
                        >
                            <Code2 size={18} />
                        </button>
                    </OverlayTrigger>
                    
                    <OverlayTrigger placement="bottom" overlay={<Tooltip>Settings</Tooltip>}>
                        <button
                            type="button"
                            className="btn btn-light border-0"
                            style={{ padding: '8px 12px' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedEpisode(item);
                                setShowEpisodeSettings(true);
                            }}
                        >
                            <Settings size={18} />
                        </button>
                    </OverlayTrigger>
                    
                    <Dropdown as="span" onClick={(e) => e.stopPropagation()}>
                        <Dropdown.Toggle as={MenuToggle} />
                        <Dropdown.Menu>
                            <Dropdown.Item 
                                as="div" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    try {
                                        saveAs(item.full_audio_url, 'preview.mp3');
                                    } catch (error) {
                                        Sentry.captureException(error);
                                        toast.error("Something went wrong.");
                                    }
                                }}
                            >
                                <Download size={14} className="me-2" />
                                Download
                            </Dropdown.Item>
                            {item.is_published !== false ? (
                                <Dropdown.Item 
                                    as="div" 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        unpublishEpisode(index);
                                    }}
                                >
                                    <PowerOff size={14} className="me-2" />
                                    Unpublish
                                </Dropdown.Item>
                            ) : (
                                <Dropdown.Item 
                                    as="div" 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        publishEpisode(index);
                                    }}
                                >
                                    <Power size={14} className="me-2" />
                                    Publish
                                </Dropdown.Item>
                            )}
                            {showDetails && !showDetails.is_automatic && (
                                <Dropdown.Item 
                                    as="div" 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowConfirmation(true);
                                    }}
                                >
                                    <Trash2 size={14} className="me-2" />
                                    Delete
                                </Dropdown.Item>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>
        </li>
    );
};

export default PodcastEpisodeItem;

